<!--
  CalculatorPdfDialog — the viewer every calculator shows after generating a PDF.

  One component rather than one per page, because the actions are identical and a borrower
  should get the same document however the LO got there.

  ON "SEND": CGS emails and texts the PDF through /api/emailmessaging/scheduleemailmessage and
  /api/cgssms/sendsmsv2, both of which require a `contact_id` from a CGS contact record. These
  calculators are not contact-scoped — there is no contact to send to — so instead of a Send
  button that cannot work, this offers the hosted link, which is the same URL the SMS would
  have contained. Paste it into an email and the borrower gets the identical document.
-->
<template>
  <teleport to="body">
    <div v-if="open" class="cpd-scrim" @mousedown.self="$emit('close')">
      <div class="cpd" role="dialog" aria-modal="true" aria-label="Scenario PDF">
        <header class="cpd__head">
          <div>
            <p class="cpd__eyebrow">Scenario PDF</p>
            <h2>{{ title }}</h2>
          </div>
          <button type="button" class="cpd__x" title="Close" @click="$emit('close')">
            <i class="fa-light fa-xmark"></i>
          </button>
        </header>

        <div class="cpd__body">
          <div v-if="busy" class="cpd__state">
            <i class="fa-duotone fa-spinner-third fa-spin"></i>
            <p>Generating the document...</p>
          </div>

          <div v-else-if="error" class="cpd__state cpd__state--error">
            <i class="fa-duotone fa-triangle-exclamation"></i>
            <div>
              <strong>Couldn't generate the PDF</strong>
              <p>{{ error }}</p>
            </div>
          </div>

          <!-- A blob URL renders inline. When only the hosted link came back there is nothing
               to frame, so offer the link rather than an empty grey rectangle. -->
          <iframe v-else-if="blobUrl" :src="blobUrl" class="cpd__frame" title="Scenario PDF"></iframe>

          <div v-else class="cpd__state">
            <i class="fa-duotone fa-file-pdf"></i>
            <div>
              <strong>Your PDF is ready</strong>
              <p>It couldn't be previewed here, but the link below opens it.</p>
            </div>
          </div>
        </div>

        <footer class="cpd__foot">
          <div class="cpd__link">
            <template v-if="hostedUrl">
              <span class="cpd__link-label">Shareable link</span>
              <input class="cpd__link-input" type="text" readonly :value="hostedUrl" @focus="$event.target.select()" />
              <button type="button" class="cpd__btn" @click="copyLink">{{ copied ? 'Copied' : 'Copy' }}</button>
            </template>
          </div>

          <div class="cpd__actions">
            <button type="button" class="cpd__btn" :disabled="!hostedUrl" @click="openTab">Open in new tab</button>
            <button type="button" class="cpd__btn" :disabled="!blobUrl" @click="print">Print</button>
            <button type="button" class="cpd__btn cpd__btn--primary" :disabled="!blobUrl && !hostedUrl" @click="download">
              Download
            </button>
          </div>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script>
  export default {
    name: "CalculatorPdfDialog",
    props: {
      open: { type: Boolean, default: false },
      busy: { type: Boolean, default: false },
      error: { type: String, default: "" },
      blobUrl: { type: String, default: "" },
      hostedUrl: { type: String, default: "" },
      title: { type: String, default: "Your scenarios" },
      fileName: { type: String, default: "Loan_Scenarios.pdf" }
    },
    emits: ["close"],
    data () {
      return { copied: false }
    },
    methods: {
      async copyLink () {
        try {
          await navigator.clipboard.writeText(this.hostedUrl)
          this.copied = true
          setTimeout(() => (this.copied = false), 2000)
        } catch (e) {
          this.copied = false
        }
      },
      openTab () {
        // noopener: the PDF host gets no handle back onto this window.
        window.open(this.hostedUrl, "_blank", "noopener")
      },
      print () {
        // Printing the iframe keeps the user in place; a popup would be blocked half the time.
        const frame = this.$el.querySelector ? this.$el.querySelector(".cpd__frame") : null
        if (frame && frame.contentWindow) {
          frame.contentWindow.focus()
          frame.contentWindow.print()
        }
      },
      download () {
        const a = document.createElement("a")
        a.href = this.blobUrl || this.hostedUrl
        a.download = this.fileName
        if (!this.blobUrl) a.target = "_blank"
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    }
  }
</script>

<style scoped>
  .cpd-scrim {
    position: fixed;
    inset: 0;
    z-index: 1200;
    background: rgba(14, 34, 68, 0.42);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }

  .cpd {
    display: flex;
    flex-direction: column;
    width: min(1000px, 100%);
    height: min(88vh, 100%);
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 18px 48px rgba(9, 24, 48, 0.28);
    overflow: hidden;
  }

  .cpd__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 20px;
    border-bottom: 1px solid #eaeaea;
  }

  .cpd__eyebrow {
    margin: 0;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #a5822a;
  }

  .cpd__head h2 {
    margin: 4px 0 0;
    font-size: 19px;
    font-weight: 700;
    color: #1f2a44;
  }

  .cpd__x {
    border: 0;
    background: none;
    font-size: 18px;
    color: #7b8397;
    cursor: pointer;
    line-height: 1;
    padding: 4px;
  }

  .cpd__x:hover { color: #1f2a44; }

  .cpd__body {
    flex: 1 1 auto;
    min-height: 0;
    background: #f7f8fb;
    display: flex;
  }

  .cpd__frame {
    flex: 1 1 auto;
    width: 100%;
    border: 0;
  }

  .cpd__state {
    margin: auto;
    display: flex;
    align-items: flex-start;
    gap: 14px;
    max-width: 460px;
    padding: 32px;
    text-align: left;
    color: #7b8397;
    font-size: 14px;
  }

  .cpd__state i { font-size: 26px; color: #5f79c7; }
  .cpd__state strong { display: block; color: #1f2a44; font-size: 15px; margin-bottom: 3px; }
  .cpd__state p { margin: 0; line-height: 1.55; }

  .cpd__state--error i { color: #a4472f; }
  .cpd__state--error strong { color: #8c2f1d; }

  .cpd__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    padding: 14px 20px;
    border-top: 1px solid #eaeaea;
    background: #ffffff;
  }

  .cpd__link {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1 1 320px;
  }

  .cpd__link-label {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #7b8397;
    white-space: nowrap;
  }

  .cpd__link-input {
    flex: 1 1 auto;
    min-width: 0;
    height: 34px;
    border: 1px solid #eaeaea;
    border-radius: 7px;
    padding: 0 10px;
    font: inherit;
    font-size: 13px;
    color: #1f2a44;
    background: #f7f8fb;
  }

  .cpd__actions {
    display: flex;
    gap: 8px;
    flex: 0 0 auto;
  }

  .cpd__btn {
    height: 36px;
    padding: 0 16px;
    border-radius: 8px;
    border: 1px solid #eaeaea;
    background: #ffffff;
    color: #123a71;
    font: inherit;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
  }

  .cpd__btn:hover:not(:disabled) { background: #f7f8fb; }

  .cpd__btn--primary {
    background: #123a71;
    border-color: #123a71;
    color: #ffffff;
  }

  .cpd__btn--primary:hover:not(:disabled) { filter: brightness(1.18); }

  .cpd__btn:disabled { opacity: 0.45; cursor: not-allowed; }

  @media (max-width: 720px) {
    .cpd-scrim { padding: 0; }
    .cpd { height: 100%; border-radius: 0; }
    .cpd__foot { justify-content: stretch; }
    .cpd__actions { flex: 1 1 auto; }
    .cpd__btn { flex: 1 1 auto; }
  }
</style>
